import { useState } from "react";
import type { ReceiptData, PrintSettings as ReceiptSettings } from "../../types/print-setting"
import {
    A4Preview,
    ThermalPreview,
} from "../../components/ReceiptTemplates";
import { DEFAULT_PRINT_SETTINGS } from "../../constants";


const SAMPLE_RECEIPT: ReceiptData = {
    // businessName: "Williams Court Hotel & Suites",
    address: "15 Unity Road, Lagos",
    phone: "+234 812 345 6789",
    email: "test@wiventory.com",
    website: "www.wiventory.com",
    // receiptNo: "RCPT-2025-0219",
    guest: "John Adewale",
    items: [
        { desc: "Room (Standard) - 2 nights", amount: "₦40,000" },
        { desc: "Breakfast Buffet (2)", amount: "₦5,000" },
        { desc: "Laundry Service", amount: "₦3,000" },
    ],
    total: "₦51,600",
    vat: "₦3,600",
    discount: "₦3,600",
    paymentMethod: "POS",
    reference: "8765-PO5-TRX",
    issuedBy: "Wisdom Okposin",
};

type Props = {
    initialSettings?: Partial<ReceiptSettings>;
    onSave?: (settings: ReceiptSettings) => void;
};

export default function PrintSettings({ initialSettings, onSave }: Props) {
    const [settings, setSettings] = useState({
        ...DEFAULT_PRINT_SETTINGS,
        ...initialSettings,
    });
    const [previewType, setPreviewType] = useState<"A4" | "THERMAL_58" | "THERMAL_80">("A4");

    const update = (partial: Partial<ReceiptSettings>) =>
        setSettings((s) => ({ ...s, ...partial }));
    const resetDefaults = () => setSettings(DEFAULT_PRINT_SETTINGS);
    const handleSave = () => onSave && onSave(settings);

    return (
        <div className="min-h-screen">
            <div className="max-w-6xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left column - controls */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Printer & Layout */}
                        <div className="bg-white p-4 rounded-2xl shadow">
                            <h2 className="text-lg font-medium mb-3">
                                Printer & Layout
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm mb-1">
                                        Printer Type
                                    </label>
                                    <select
                                        value={settings.printerType}
                                        onChange={(e) => {
                                            const type = e.target.value;
                                            if (type === "A4") setPreviewType("A4");
                                            else setPreviewType("THERMAL_80")
                                            update({ printerType: type as "A4" | "THERMAL_58" | "THERMAL_80" })
                                        }}
                                        className="w-full border rounded px-3 py-2"
                                    >
                                        <option value="A4">A4</option>
                                        <option value="THERMAL_58">Thermal</option>
                                        {/* <option value="THERMAL_58">Thermal (58mm)</option>
                                        <option value="THERMAL_80">Thermal (80mm)</option> */}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm mb-1">
                                        Default Print Copies
                                    </label>
                                    <input
                                        type="number"
                                        value={settings.defaultCopies}
                                        min={1}
                                        onChange={(e) =>
                                            update({ defaultCopies: Number(e.target.value) })
                                        }
                                        className="w-full border rounded px-3 py-2"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm mb-1">
                                        Orientation (A4)
                                    </label>
                                    <select
                                        value={settings.orientation}
                                        onChange={(e) =>
                                            update({
                                                orientation: e.target.value as "portrait" | "landscape"
                                            })
                                        }
                                        disabled={settings.printerType !== "A4"}
                                        className="w-full border rounded px-3 py-2 disabled:opacity-50"
                                    >
                                        <option value="portrait">Portrait</option>
                                        <option value="landscape">Landscape</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm mb-1">
                                        Paper Width (Thermal)
                                    </label>
                                    <select
                                        value={settings.paperWidth}
                                        onChange={(e) =>
                                            update({
                                                paperWidth: e.target.value as "58mm" | "80mm"
                                            })
                                        }
                                        disabled={settings.printerType === "A4"}
                                        className="w-full border rounded px-3 py-2 disabled:opacity-50"
                                    >
                                        <option value="58mm">58mm</option>
                                        <option value="80mm">80mm</option>
                                    </select>
                                </div>

                                <div className="md:col-span-2 flex items-center space-x-3">
                                    <label className="text-sm">
                                        Include Duplicate (A4 only)
                                    </label>
                                    <input
                                        type="checkbox"
                                        checked={!!settings.includeDuplicate}
                                        onChange={(e) =>
                                            update({ includeDuplicate: e.target.checked })
                                        }
                                        // disabled={settings.printerType !== "A4"}
                                        className="form-checkbox h-5 w-5"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Content & Display */}
                        <div className="bg-white p-4 rounded-2xl shadow">
                            <h2 className="text-lg font-medium mb-3">Content & Display</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {/* Toggle Controls */}
                                {[
                                    { label: "Show Business Logo", key: "showLogo" },
                                    // { label: "Show VAT/Tax Summary", key: "showVat" },
                                    { label: "Include Address", key: "includeAddress" },
                                    { label: "Show Customer Signature", key: "showSignature" },
                                    { label: 'Show "Thank You" Note', key: "showFooterNote" },
                                    { label: "Show Customer Copy Label", key: "showCustomerCopyLabel" },
                                    { label: "Show Business Copy Label", key: "showBusinessCopyLabel" },
                                ].map((item) => (
                                    <div key={item.key} className="flex items-center justify-between">
                                        <div>
                                            <div className="text-sm">{item.label}</div>
                                        </div>
                                        <input
                                            type="checkbox"
                                            checked={settings[item.key as keyof ReceiptSettings] as boolean}
                                            onChange={(e) =>
                                                update({ [item.key]: e.target.checked } as any)
                                            }
                                            className="form-checkbox h-5 w-5  cursor-pointer"
                                        />
                                    </div>
                                ))}

                                <div className="md:col-span-2">
                                    <label className="block text-sm mb-1">
                                        Display Business Name
                                    </label>
                                    <input
                                        type="text"
                                        value={settings.displayName}
                                        onChange={(e) => update({ displayName: e.target.value })}
                                        className="w-full border rounded px-3 py-2"
                                        placeholder="Enter short name e.g. Wiventory Hotel & Suites"
                                    />
                                </div>

                                <div className="md:col-span-2">
                                    <label className="block text-sm mb-1">
                                        Customer Copy Label
                                    </label>
                                    <input
                                        type="text"
                                        value={settings.customerCopyLabel}
                                        onChange={(e) => update({ customerCopyLabel: e.target.value })}
                                        className="w-full border rounded px-3 py-2"
                                        placeholder="Enter Customer Copy text"
                                        disabled={!settings.showCustomerCopyLabel}
                                    />
                                </div>

                                <div className="md:col-span-2">
                                    <label className="block text-sm mb-1">
                                        Business Copy Label
                                    </label>
                                    <input
                                        type="text"
                                        value={settings.businessCopyLabel}
                                        onChange={(e) => update({ businessCopyLabel: e.target.value })}
                                        className="w-full border rounded px-3 py-2"
                                        placeholder="Enter Business Copy text"
                                        disabled={!settings.showBusinessCopyLabel}
                                    />
                                </div>


                                <div className="md:col-span-2">
                                    <label className="block text-sm mb-1">Custom Footer Text</label>
                                    <input
                                        type="text"
                                        value={settings.footerNote}
                                        onChange={(e) => update({ footerNote: e.target.value })}
                                        className="w-full border rounded px-3 py-2"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm mb-1">Receipt Font Size</label>
                                    <select
                                        value={settings.fontSize}
                                        onChange={(e) => update({
                                            fontSize: e.target.value as "small" | "medium" | "large"
                                        })}
                                        className="w-full border rounded px-3 py-2"
                                    >
                                        <option value="small">Small</option>
                                        <option value="medium">Medium</option>
                                        <option value="large">Large</option>
                                    </select>
                                </div>
                            </div>
                        </div>


                        <div className="flex items-center space-x-3">
                            <button
                                onClick={handleSave}
                                className="bg-blue-600 text-white px-4 py-2 rounded-lg shadow"
                            >
                                Save Changes
                            </button>
                            <button
                                onClick={resetDefaults}
                                className="border px-4 py-2 rounded-lg shadow bg-white"
                            >
                                Reset to Defaults
                            </button>
                        </div>
                    </div>

                    {/* Right column - preview */}
                    <div className="space-y-6">
                        <div className="bg-white p-4 rounded-2xl shadow">
                            <h3 className="font-medium mb-3">Preview</h3>

                            {/* <div className="flex space-x-2 mb-3">
                                <button
                                    onClick={() => setPreviewType("A4")}
                                    className={`px-3 py-1 rounded ${previewType === "A4"
                                        ? "bg-blue-600 text-white"
                                        : "border"
                                        }`}
                                >
                                    A4
                                </button>
                                <button
                                    onClick={() => setPreviewType("THERMAL_58")}
                                    className={`px-3 py-1 rounded ${previewType === "THERMAL_58"
                                        ? "bg-blue-600 text-white"
                                        : "border"
                                        }`}
                                >
                                    Thermal 58mm
                                </button>
                                <button
                                    onClick={() => setPreviewType("THERMAL_80")}
                                    className={`px-3 py-1 rounded ${previewType === "THERMAL_80"
                                        ? "bg-blue-600 text-white"
                                        : "border"
                                        }`}
                                >
                                    Thermal
                                </button>
                            </div> */}

                            <div>
                                {previewType === "A4" && (
                                    <div>
                                        <div className="mb-2 text-xs text-gray-500">
                                            {/* A4 preview shows original + duplicate on one page. */}
                                        </div>
                                        <div className="overflow-auto">
                                            <A4Preview settings={settings} sample={SAMPLE_RECEIPT} />
                                        </div>
                                    </div>
                                )}

                                {previewType === "THERMAL_58" && (
                                    <div>
                                        <div className="mb-2 text-xs text-gray-500">
                                            {/* Thermal preview. Print once for a single receipt. */}
                                        </div>
                                        <div className="overflow-auto flex justify-center">
                                            <ThermalPreview
                                                settings={settings}
                                                sample={SAMPLE_RECEIPT}
                                            />
                                        </div>
                                    </div>
                                )}

                                {previewType === "THERMAL_80" && (
                                    <div>
                                        <div className="mb-2 text-xs text-gray-500">
                                            {/* Thermal preview. Print once for a single receipt. */}
                                        </div>
                                        <div className="overflow-auto flex justify-center">
                                            <ThermalPreview
                                                settings={settings}
                                                sample={SAMPLE_RECEIPT}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mt-6 text-xs text-gray-500">
                    Tip: Toggle "Show VAT/Tax Summary" off for businesses that don't
                    require VAT on receipts.
                </div>
            </div>
        </div>
    );
}
